import { untilDestroyed } from '@ngneat/until-destroy';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ClientData } from '../client.data';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { AppBarTitleService, AppBarActionsService, AppBarAction, FormHelper, MessageService, AppPageService, SimpleDialogService, CommonActionKey } from 'common';
import { ClientService } from '../client.service';
import { MatDialog } from '@angular/material/dialog';
import { ClientUpdateDialogComponent } from '../client-update-dialog/client-update-dialog.component';

@UntilDestroy()
@Component({
    selector: 'ifb-client-details',
    templateUrl: './client-details.component.html',
    styleUrls: ['./client-details.component.scss'],
    standalone: false
})
export class ClientDetailsComponent implements OnInit {
  private saveSub: Subscription;
  private deleteSub: Subscription;

  data: ClientDetailsComponentData;
  form: UntypedFormGroup;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly clientService: ClientService,
    private readonly appBarTitleService: AppBarTitleService,
    private readonly appBarActionsService: AppBarActionsService,
    private readonly appPageService: AppPageService,
    private readonly dialog: MatDialog,
    private readonly messageService: MessageService,
    private readonly simpleDialogService: SimpleDialogService
  ) {
    this.formInit();
  }

  ngOnInit(): void {
    this.route.data.pipe(untilDestroyed(this)).subscribe(this.initData.bind(this));
    this.appBarActionsService.invoking.pipe(untilDestroyed(this)).subscribe(this.actionDispatch.bind(this));
    this.form.statusChanges.pipe(untilDestroyed(this)).subscribe(this.actionUpdate.bind(this));
    this.initActions();
  }

  private initActions(): void {
    setTimeout(() => {
      this.appBarActionsService.actions = [
        { id: 'delete', label: 'Delete', buttonType: 'button', disabled: true },
        { id: 'cancel', label: 'Cancel', buttonType: 'button', disabled: false },
        { id: 'save', label: 'Save', buttonType: 'submit', buttonAppearance: 'flat', buttonColor: 'primary', disabled: false }
      ];
      this.actionUpdate();
    }, 0);
  }

  private actionDispatch(action: AppBarAction): void {
    const actionHandler: (action: AppBarAction) => void = this[action.id].bind(this);
    actionHandler(action);
  }

  private actionUpdate(): void {
    this.appBarActionsService.enable('delete', this.data && this.data.client && this.data.client.id && !this.deleteSub);
  }

  cancel(): void {
    this.appPageService.back();
  }

  private initData(data: ClientDetailsComponentData): void {
    this.data = data;
    if (data.client)
      this.form.patchValue(data.client);

    this.appBarTitleService.title = data.client && data.client.id
      ? `Client ${data.client.name}`
      : 'Add new client';

    this.form.markAsPristine();

    this.actionUpdate();
  }

  delete(): void {
    if (!this.data || !this.data.client || !this.data.client.id)
      this.appPageService.back();

    const message = `Delete client ${this.form.value.name}?`;
    this.simpleDialogService.openSimpleConfirmDialog(message, "Confirm")
      .subscribe((result: CommonActionKey) => {
        if (result !== CommonActionKey.Confirm) {
          this.actionUpdate();
          return;
        }

        this.deleteSub = this.clientService.delete(this.data.client.id).subscribe(() => {
          this.appPageService.back();
        });
      });
  }

  private formInit(): void {
    this.form = this.formBuilder.group({
      id: undefined,
      name: [undefined, Validators.compose([Validators.required, Validators.maxLength(64)])],
      oidcAudience: [undefined, Validators.compose([Validators.maxLength(64)])],
      oidcLoginAllowed: [undefined, Validators.required],
      active: [undefined, Validators.required]
    });
  }

  save(): void {
    FormHelper.showInvalidFormFields(this.form);
    if (this.saveSub)
      return;
    if (!this.form.valid)
      return;

    if (!this.form.dirty) {
      this.cancel();
      return;
    }

    if (this.data?.client?.id) {
      ClientUpdateDialogComponent.show(this.dialog, this.form, "Update").subscribe(res => {
        if (res) {
          this.saveSub = this.clientService.update(this.form.value)
            .subscribe({
              next: this.saveSuccessHandler.bind(this),
              error: this.saveErrorHandler.bind(this)
            });
          this.actionUpdate();
          this.appPageService.back();
        }
      });
    } else {
      ClientUpdateDialogComponent.show(this.dialog, this.form, "Create").subscribe(res => {
        if (res) {
          this.saveSub = this.clientService.create(this.form.value)
            .subscribe({
              next: this.saveSuccessHandler.bind(this),
              error: this.saveErrorHandler.bind(this)
            });
        }
        this.actionUpdate();
      });
    }
  }

  private saveSubClear(): void {
    if (this.saveSub)
      this.saveSub.unsubscribe();
    this.saveSub = null;
  }

  private saveErrorHandler(error: any): void {
    this.saveSubClear();
    this.messageService.error(error);
  }

  private saveSuccessHandler(client: ClientData): void {
    if (this.saveSub)
      this.saveSub.unsubscribe();
    this.saveSub = null;

    if (this.data.client) {
      this.data.client = this.form.value;
      this.initData(this.data);
      this.actionUpdate();
    } else {
      this.router.navigate(['admin', 'client', client.id], { replaceUrl: true });
    }
    this.saveSubClear();
    this.appPageService.back();
  }
}

export interface ClientDetailsComponentData {
  client: ClientData;
}
